import { useCallback } from "react";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { getFileViewUrl } from "@/features/files/services/sharepointService";
import { parseAppLink, showAppLinkNotice } from "@/shared/utils/appLinks";

const isUnavailableError = (err: unknown) =>
  axios.isAxiosError(err) && (err.response?.status === 403 || err.response?.status === 404);

/**
 * Returns a handler that opens one of our own links inside the app:
 * - announcement → the home feed, scrolled to that announcement;
 * - file → a fresh view URL in a new tab, leaving the user where they clicked;
 * - folder → the Files page for that folder.
 * It returns false for any other URL so the caller keeps its default behaviour.
 */
export const useOpenAppLink = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return useCallback(
    (url: string | null | undefined): boolean => {
      const link = parseAppLink(url);
      if (!link) return false;

      if (link.type === "announcement") {
        navigate(`/?announcement=${link.rowId}`, { state: { focusId: String(link.rowId) } });
        return true;
      }

      if (!link.fileId) {
        navigate(`/files/${encodeURIComponent(link.folderId)}`);
        return true;
      }

      // Open the tab now, while we're still inside the click — a window.open
      // after the request finishes would be popup-blocked. The view URL is
      // requested on every click and never cached: it is (or will be) a
      // short-lived signed URL.
      const win = window.open("", "_blank");
      if (win) win.opener = null;
      getFileViewUrl(link.fileId)
        .then((viewUrl) => {
          if (win) win.location.href = viewUrl;
          else window.open(viewUrl, "_blank", "noopener,noreferrer");
        })
        .catch((err) => {
          console.error("Failed to open linked file:", err);
          win?.close();
          showAppLinkNotice(
            isUnavailableError(err) ? t("sharepoint.file_unavailable") : t("sharepoint.file_open_failed"),
          );
        });
      return true;
    },
    [navigate, t],
  );
};

/** onClick for an <a>: opens our links in place, leaves every other link alone. */
export const useAppLinkClickHandler = () => {
  const openAppLink = useOpenAppLink();
  return useCallback(
    (e: MouseEvent<HTMLAnchorElement>, href: string | null | undefined) => {
      // Cmd/Ctrl/Shift/middle click: let the browser open a new tab as usual.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      if (openAppLink(href)) e.preventDefault();
    },
    [openAppLink],
  );
};
